import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Pressable, type LayoutChangeEvent } from 'react-native';

import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { createStyles } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import type { KitNotesTabKey } from './types';

/** Mirrors the app tab bar's indicator spring (`src/components/ui/tabbar`). */
const SPRING_CONFIG = { damping: 30, stiffness: 300, mass: 0.8 };

export type KitNotesTab = {
  key: KitNotesTabKey;
  label: string;
};

type KitNotesTabBarProps = {
  tabs: readonly KitNotesTab[];
  activeKey: KitNotesTabKey;
  onChange: (key: KitNotesTabKey) => void;
};

type TabItemProps = {
  tab: KitNotesTab;
  index: number;
  isActive: boolean;
  onChange: (key: KitNotesTabKey) => void;
  onItemLayout: (index: number, event: LayoutChangeEvent) => void;
};

const TabItem = memo(({ tab, index, isActive, onChange, onItemLayout }: TabItemProps) => {
  const styles = useStyles();

  const handlePress = useCallback(() => {
    if (!isActive) onChange(tab.key);
  }, [isActive, onChange, tab.key]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => onItemLayout(index, event),
    [index, onItemLayout],
  );

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      onPress={handlePress}
      onLayout={handleLayout}
      style={styles.tabItem}>
      <Text
        variant={
          isActive
            ? 'Font Guardian App/Subtitle/Subtitle 14 Semibold'
            : 'Font Guardian App/Subtitle/Subtitle 14 Regular'
        }
        numberOfLines={1}
        style={isActive ? styles.tabLabelActive : styles.tabLabel}>
        {tab.label}
      </Text>
    </Pressable>
  );
});

TabItem.displayName = 'KitNotesTabItem';

/**
 * Full-width underline tabs on a white bar, per the KIT & Notes design.
 * The active underline slides between tabs (same spring as the app tab bar).
 */
function KitNotesTabBar({ tabs, activeKey, onChange }: KitNotesTabBarProps) {
  const styles = useStyles();
  const activeIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.key === activeKey),
  );

  const itemLayouts = useRef<Record<number, { x: number; width: number }>>({});
  const hasInitialized = useRef(false);
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const indicatorLeft = useSharedValue(0);
  const indicatorWidth = useSharedValue(0);

  const indicatorStyle = useAnimatedStyle(() => ({
    left: indicatorLeft.value,
    width: indicatorWidth.value,
  }));

  const moveToIndex = useCallback(
    (index: number, animated: boolean) => {
      const layout = itemLayouts.current[index];
      if (!layout) return;

      if (animated) {
        indicatorLeft.value = withSpring(layout.x, SPRING_CONFIG);
        indicatorWidth.value = withSpring(layout.width, SPRING_CONFIG);
      } else {
        indicatorLeft.value = layout.x;
        indicatorWidth.value = layout.width;
      }
    },
    [indicatorLeft, indicatorWidth],
  );

  // First layout pass positions the underline instantly; later passes (e.g. rotation) re-track it.
  const handleItemLayout = useCallback(
    (index: number, event: LayoutChangeEvent) => {
      const { x, width } = event.nativeEvent.layout;
      itemLayouts.current[index] = { x, width };

      if (index === activeIndexRef.current) {
        moveToIndex(index, hasInitialized.current);
        hasInitialized.current = true;
      }
    },
    [moveToIndex],
  );

  useEffect(() => {
    moveToIndex(activeIndex, true);
  }, [activeIndex, moveToIndex]);

  return (
    <Stack style={styles.track}>
      <Stack direction="row">
        {tabs.map((tab, index) => (
          <TabItem
            key={tab.key}
            tab={tab}
            index={index}
            isActive={tab.key === activeKey}
            onChange={onChange}
            onItemLayout={handleItemLayout}
          />
        ))}
      </Stack>
      <Animated.View style={[styles.indicator, indicatorStyle]} />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  track: {
    backgroundColor: colors['0'],
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['3'],
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: scale(10),
  },
  indicator: {
    position: 'absolute',
    bottom: -1,
    height: 1.5,
    backgroundColor: guardianColors['Main/Main'],
  },
  tabLabel: {
    color: guardianColors['10'],
  },
  tabLabelActive: {
    color: guardianColors['Main/Main'],
  },
}));

export default memo(KitNotesTabBar);
